// The question every destructive action asks first: what will happen, one button that does it and
// one that does not. A Sheet like every other popup, so focus is trapped, Escape and the close
// button cancel, and the confirm button locks while its work runs so a second tap cannot repeat it.
// An action that fails keeps the sheet open with the error, so nobody walks away believing it worked.
import { useCallback, useState } from "react";
import { useT } from "../lib/i18n";
import { Body, Button, Column, ErrorBanner, Sheet, Subheading } from "../ui";

export interface ConfirmSheetProps {
  title: string;
  body: string;
  confirmLabel: string;
  /** Defaults to the shared "Cancel". */
  cancelLabel?: string;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
  testId?: string;
}

export function ConfirmSheet({
  title,
  body,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  testId = "confirm-sheet",
}: ConfirmSheetProps): React.JSX.Element {
  const { t } = useT();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const confirm = (): void => {
    if (busy) return;
    setBusy(true);
    setFailed(false);
    // An async wrapper, so a callback that throws before its first await is a rejection too.
    (async () => onConfirm())()
      .catch(() => {
        setFailed(true);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <Sheet label={title} onClose={onCancel} testId={testId}>
      <Subheading render="h2">{title}</Subheading>
      <Body>{body}</Body>
      {failed && <ErrorBanner message={t("common.error.generic")} />}
      <Column gap="2">
        <Button
          full
          tone="danger"
          label={confirmLabel}
          disabled={busy}
          data-testid={`${testId}-yes`}
          onPress={confirm}
        />
        <Button
          full
          tone="secondary"
          label={cancelLabel ?? t("people.confirm.no")}
          data-testid={`${testId}-no`}
          onPress={onCancel}
        />
      </Column>
    </Sheet>
  );
}

export interface ConfirmRequest {
  title: string;
  body: string;
  confirmLabel: string;
  onConfirm: () => void | Promise<void>;
}

/** Hands a screen `ask(request)` and the sheet to render; the sheet closes itself once the action
 * succeeds, and stays open with an error when it fails. */
export function useConfirm(): {
  ask: (request: ConfirmRequest) => void;
  confirmation: React.ReactNode;
} {
  const [request, setRequest] = useState<ConfirmRequest | undefined>(undefined);
  const ask = useCallback((next: ConfirmRequest): void => {
    setRequest(next);
  }, []);
  const close = (): void => {
    setRequest(undefined);
  };
  const confirmation =
    request === undefined ? null : (
      <ConfirmSheet
        title={request.title}
        body={request.body}
        confirmLabel={request.confirmLabel}
        onCancel={close}
        onConfirm={async () => {
          await request.onConfirm();
          close();
        }}
      />
    );
  return { ask, confirmation };
}
